/**
 * Credential redaction for catalog stream URLs, shared by the server and the
 * APK (Section 22 Phase 1, step 3).
 *
 * Catalogs are cached — on the server today, and in the device's IndexedDB once
 * the APK builds them itself — so stream URLs are stored with the viewer's
 * credentials stripped, and the credentials are put back only at the moment of
 * playback. Keeping ONE implementation here means the server and the device can
 * never disagree about what a redacted URL looks like.
 *
 * Moved verbatim from services/api (redactCatalog in source-sync.ts,
 * hydrateRedactedUrl in stream-proxy.ts); behaviour is unchanged.
 */
const REDACTED = "[redacted]";
// "timeshift" carries the credentials in the same two path segments, so catch-up
// URLs redact and hydrate exactly like live ones (Section 16, measured 2026-09-17).
const XTREAM_KINDS = ["live", "movie", "series", "timeshift"];
const USERNAME_QUERY_KEY = /^user(?:name)?$/i;
const PASSWORD_QUERY_KEY = /^(?:pass(?:word)?|pwd)$/i;
const OTHER_SECRET_QUERY_KEY = /^(?:token|auth|api[_-]?key)$/i;
/**
 * Take the viewer's username and password out of one stream URL, leaving a
 * `[redacted]` placeholder that hydrateRedactedUrl fills back in at playback:
 * the Xtream path segments (/live|movie|series/{username}/{password}/{id}),
 * user:pass@ userinfo, and user/username and pass/password/pwd query parameters.
 * A value that is not a URL is returned untouched — malformed provider URLs are
 * handled by the sync error path.
 *
 * Until 2026-09-17 userinfo and query credentials were DELETED, not replaced,
 * so an M3U source that puts them there could never play (owner decision: M3U
 * must play, "even if it means putting the password back in"). Token, auth and
 * api-key parameters are left in place for the same reason: they are per-stream
 * values nothing could restore, and a catalog only ever goes to the viewer whose
 * source it is.
 */
export function redactStreamUrl(value) {
    try {
        const url = new URL(value);
        const segments = url.pathname.split("/").filter(Boolean);
        if (segments.length >= 4 && XTREAM_KINDS.includes(segments[0] ?? "")) {
            url.pathname = `/${segments[0]}/${REDACTED}/${REDACTED}/${segments.slice(3).join("/")}`;
        }
        if (url.username)
            url.username = REDACTED;
        if (url.password)
            url.password = REDACTED;
        for (const key of [...new Set(url.searchParams.keys())]) {
            if (USERNAME_QUERY_KEY.test(key) || PASSWORD_QUERY_KEY.test(key))
                url.searchParams.set(key, REDACTED);
        }
        return url.toString();
    }
    catch {
        // Leave a non-URL value untouched rather than throwing during redaction.
    }
    return value;
}
/**
 * The EPG URL a catalog echoes back is never played from, so it keeps the old,
 * stricter rule: every credential, token included, is removed outright.
 */
function stripUrlCredentials(value) {
    try {
        const url = new URL(value);
        url.username = "";
        url.password = "";
        for (const key of [...new Set(url.searchParams.keys())]) {
            if (USERNAME_QUERY_KEY.test(key) || PASSWORD_QUERY_KEY.test(key) || OTHER_SECRET_QUERY_KEY.test(key))
                url.searchParams.delete(key);
        }
        return url.toString();
    }
    catch {
        return value;
    }
}
/** Redact every stream URL in a catalog (channels, VOD, series episodes) and its EPG URL. */
export function redactCatalog(catalog) {
    return {
        ...catalog,
        ...(catalog.epgUrl ? { epgUrl: stripUrlCredentials(catalog.epgUrl) } : {}),
        channels: catalog.channels.map((item) => ({ ...item, streamUrl: redactStreamUrl(item.streamUrl) })),
        vod: catalog.vod.map((item) => ({ ...item, streamUrl: redactStreamUrl(item.streamUrl) })),
        series: catalog.series.map((item) => ({
            ...item,
            seasons: item.seasons.map((season) => ({
                ...season,
                episodes: season.episodes.map((episode) => ({ ...episode, streamUrl: redactStreamUrl(episode.streamUrl) })),
            })),
        })),
    };
}
/**
 * Put the viewer's credentials back into a redacted Xtream stream URL, at the
 * moment of playback. Only the path form is restored (see the note on
 * redactStreamUrl). Without both credentials the URL is returned unchanged, so
 * a missing credential fails loudly upstream instead of being guessed.
 */
export function hydrateRedactedUrl(rawUrl, username, password) {
    if (!username || !password || !rawUrl.includes(encodeURIComponent(REDACTED)) && !rawUrl.includes(REDACTED))
        return rawUrl;
    let url;
    try {
        url = new URL(rawUrl);
    }
    catch {
        return rawUrl;
    }
    let changed = false;
    const segments = url.pathname.split("/").filter(Boolean);
    if (segments.length >= 4 && XTREAM_KINDS.includes(segments[0] ?? "")) {
        if (segments[1] === REDACTED)
            segments[1] = encodeURIComponent(username);
        if (segments[2] === REDACTED)
            segments[2] = encodeURIComponent(password);
        url.pathname = `/${segments.join("/")}`;
        changed = true;
    }
    if (isRedacted(url.username)) {
        url.username = encodeURIComponent(username);
        changed = true;
    }
    if (isRedacted(url.password)) {
        url.password = encodeURIComponent(password);
        changed = true;
    }
    for (const key of [...new Set(url.searchParams.keys())]) {
        if (url.searchParams.get(key) !== REDACTED)
            continue;
        if (USERNAME_QUERY_KEY.test(key))
            url.searchParams.set(key, username);
        else if (PASSWORD_QUERY_KEY.test(key))
            url.searchParams.set(key, password);
        else
            continue;
        changed = true;
    }
    return changed ? url.toString() : rawUrl;
}
/** A userinfo part holding the placeholder (URL keeps it percent-encoded). */
function isRedacted(part) {
    if (!part)
        return false;
    try {
        return decodeURIComponent(part) === REDACTED;
    }
    catch {
        return false;
    }
}
//# sourceMappingURL=redaction.js.map